import { useContext, useEffect, useMemo, useState } from 'react'
import { UIContext } from 'Context/UI'
import { BusinessLocationSelectionContext } from '../BusinessLocationSelection/Context'
import { useNavigate } from 'react-router-dom'
import { useProfileStore } from 'Store/Profile'
import { useCallbackPrompt } from 'Hooks/useCallbackPrompt'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { TABS } from '../Config'
import { htmlToText } from 'html-to-text'
import { generateImageUrl } from 'Utils/generateImageUrl'

const VIEW_INDEX = 0

const ViewModel = () => {
	const deliveryDateAmountOptions: { label: string; value: number }[] = [...Array(60)].reduce(
		(acc, current, idx) => {
			if (idx + 1 >= 3) {
				acc.push({
					label: `${idx + 1} วัน`,
					value: idx + 1,
				})
			}

			return acc
		},
		[]
	)
	const validationSchema = Yup.object().shape({
		slug: Yup.string()
			.matches(/^(?!([-_]))[a-zA-Z0-9._-]+$/, 'กรุณากรอก URL SLUG ให้ถูกต้อง')
			.min(3, 'URL SLUG ต้องยาวอย่างน้อย 3 ตัวอักษร')
			.max(20, 'URL SLUG มีความยาวเกินกำหนด')
			.required('กรุณากรอก URL SLUG ของคุณ'),
		displayTitle: Yup.string()
			.min(1, 'กรุณากรอกชื่อร้านค้าของคุณ')
			.required('กรุณากรอกชื่อร้านค้าของคุณ'),
		contactPersonFullname: Yup.string()
			.min(1, 'กรุณากรอกชื่อผู้ติดต่อ')
			.required('กรุณากรอกชื่อผู้ติดต่อ'),
		contactPersonPosition: Yup.string()
			.min(1, 'กรุณากรอกตำแหน่งผู้ติดต่อ')
			.required('กรุณากรอกตำแหน่งผู้ติดต่อ'),
		email: Yup.string().email('กรุณากรอกอีเมลให้ถูกต้อง').required('กรุณากรอกอีเมลของคุณ'),
		website: Yup.string().url('กรุณากรอก Website URL ให้ถูกต้อง'),
	})

	const navigate = useNavigate()
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const {
		setOpen: setOpenSelectLocation,
		changeConfirm,
		setChangeConfirm,
		position,
		setPosition,
	} = useContext(BusinessLocationSelectionContext)
	const { profile, getProfile, updateProfile } = useProfileStore(
		state => ({
			profile: state.profile,
			getProfile: state.getProfile,
			updateProfile: state.updateProfile,
		}),
		shallow
	)
	const [formState, setFormState] = useState({
		id: 0,
		code: '',
		slug: '',
		displayTitle: '',
		contactPersonFullname: '',
		contactPersonPosition: '',
		supplierType: '',
		businessDescription: '',
		email: '',
		website: '',
		companyName: '',
		companyAddress: '',
		companyTaxNo: '',
		minimumProductionQuantity: '',
		totalEmployee: 0,
		companyPictureCover: null,
		companyLogo: null,
		companyCard: null,
		companyFactoryPicture: null,
		companyVatRegistrationFile: null,
		companyCertificateFile: null,
		companyIsoFile: null,
		companyOtherFile: null,
		latitude: 13.7563,
		longitude: 100.5018,
		supplierFeatures: [],
		tagline: '',
		cardDescription: '',
		deliveryDateAmount: 0,
		isVerified: '',
	})
	const [hasChanged, setHasChanged] = useState(false)
	const [error, setError] = useState<{ [key: string]: string }>({})
	const [isSubmitting, setIsSubmitting] = useState(false)
	const isBusinessDescriptionOver = useMemo(
		() => htmlToText(formState.businessDescription).length > 300,
		[formState.businessDescription]
	)

	useCallbackPrompt(hasChanged)

	const handleOpenSelectLocation = () => {
		setPosition({
			lat: formState.latitude,
			lng: formState.longitude,
		})

		setChangeConfirm(false)
		setOpenSelectLocation(true)
	}

	const handlePositionChange = () => {
		if (changeConfirm) {
			// @ts-ignore
			setFormState(prevState => ({
				...prevState,
				latitude: position.lat,
				longitude: position.lng,
			}))
			setChangeConfirm(false)
			setHasChanged(true)
		}
	}

	const findTabPath = (tabIndex: number) => {
		const tab = TABS.find((_, idx) => idx === tabIndex)
		if (tab) {
			return tab.path
		}
	}

	const onChangeTab = (tabIndex: number) => {
		if (tabIndex !== VIEW_INDEX) {
			navigate(findTabPath(tabIndex) ?? '#')
		}
	}

	const onChange = (name: string, value: any) => {
		setFormState({
			...formState,
			[name]: value,
		})
		setHasChanged(true)
		validationSchema
			.validate(
				{
					...formState,
					[name]: value,
				},
				{ abortEarly: false }
			)
			.then(function () {
				setError({})
			})
			.catch(function (err) {
				setError(
					err.inner.reduce((all: any[], e: any) => {
						all[e.path] = e.message

						return all
					}, {})
				)
			})
	}

	const onSubmit = async () => {
		if (hasChanged) {
			setIsSubmitting(true)
			const requiredFields = [
				'slug',
				'displayTitle',
				'contactPersonFullname',
				'contactPersonPosition',
				'email',
				'latitude',
				'longitude',
			]
			const allFields = [
				...requiredFields,
				'companyLogo',
				'website',
				'businessDescription',
				'tagline',
				'cardDescription',
				'deliveryDateAmount',
			]

			validationSchema
				.validate(formState, { abortEarly: false })
				.then(function () {
					setError({})
					const body: { [key: string]: any } = {}

					allFields.forEach(key => {
						// @ts-ignore
						if (formState[key] !== profile[key]) {
							// @ts-ignore
							body[key] = formState[key]
						}
					})

					updateProfile(body).then(({ success, data }) => {
						if (!success) {
							toast.error(data)
							setIsSubmitting(false)
							return
						} else {
							setHasChanged(false)
							getProfile().then(({ data: pData }) => {
								setFormState(pData)
								toast.success('บันทึกข้อมูลสำเร็จ')
								setIsSubmitting(false)
							})
						}
					})
				})
				.catch(function (err) {
					toast.error('กรุณากรอกข้อมูลให้ครบถ้วน')
					setError(
						err.inner.reduce((all: any[], e: any) => {
							all[e.path] = e.message

							return all
						}, {})
					)
					setIsSubmitting(false)
				})
		}
	}

	useEffect(() => {
		setShowContentToolbar(false)
		setCustomContentToolbar(null)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
			setPosition({
				lat: 13.7563,
				lng: 100.5018,
			})
			setChangeConfirm(false)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (profile) {
			setFormState({
				...profile,
				// @ts-ignore
				companyLogo: profile.companyLogo ? generateImageUrl(profile.companyLogo) : null,
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (changeConfirm) {
			handlePositionChange()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [changeConfirm])

	return {
		companyName: profile?.companyName,
		currentTab: VIEW_INDEX,
		formState,
		onChange,
		onChangeTab,
		onSubmit,
		handleOpenSelectLocation,
		error,
		isSubmitting,
		isBusinessDescriptionOver,
		deliveryDateAmountOptions,
	}
}

export default ViewModel
